import { Recipe } from '@/types/recipe';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Edit, Save, UtensilsCrossed, X } from 'lucide-react';

interface RecipeHeaderProps {
  recipe: Recipe;
  isEditing: boolean;
  editedRecipe: Recipe | null;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onRecipeChange: (updatedRecipe: Recipe) => void;
}

export const RecipeHeader = ({
  recipe,
  isEditing,
  editedRecipe,
  onEdit,
  onSave,
  onCancel,
  onRecipeChange,
}: RecipeHeaderProps) => {
  return (
    <Card>
      <CardHeader className="bg-secondary/30 border-b">
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <UtensilsCrossed className="h-5 w-5 text-primary" />
            Recipe Info
          </CardTitle>
          {!isEditing ? (
            <Button onClick={onEdit} variant="outline" size="sm" className="bg-card text-foreground">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button onClick={onSave} size="sm">
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button onClick={onCancel} variant="outline" size="sm">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-6 bg-card text-foreground">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium text-foreground">Name</Label>
            {isEditing ? (
              <Input 
                id="name"
                name="name"
                value={editedRecipe?.name || ''}
                onChange={(e) => onRecipeChange({
                  ...editedRecipe!,
                  name: e.target.value
                })}
                className="border-border bg-background text-foreground focus:border-primary"
              />
            ) : (
              <div className="p-2 bg-muted rounded-md border border-border text-foreground">{recipe.name}</div>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium text-foreground">Description</Label>
            {isEditing ? (
              <Input 
                id="description"
                name="description"
                value={editedRecipe?.description || ''}
                onChange={(e) => onRecipeChange({
                  ...editedRecipe!,
                  description: e.target.value
                })}
                className="border-border bg-background text-foreground focus:border-primary"
              />
            ) : (
              <div className="p-2 bg-muted rounded-md border border-border text-foreground">{recipe.description}</div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};